import React, { Fragment, useLayoutEffect, useMemo, useRef } from 'react';
import PropTypes from "prop-types";
import { useTable, useSortBy, useExpanded, useResizeColumns, useFlexLayout, usePagination } from 'react-table';
import Box from '../Box';
import Icon from '../Icon';
import { makeStyles } from '../../utils/styles';
import Pagination from '../Pagination';
import Divider from '../Divider';
import { StyledTableContainer, StyledTableWrapper, StyledTable, StyledThead, StyledTbody, StyledTrExpanded, StyledTr, StyledTh, StyledTd, StyledTrLink, StyledFooter } from './styled';
import { Col, Row } from 'react-styled-flexboxgrid';
import Typography from '../Typography';
import { scrollbarWidth } from '../../utils/helpers';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(css => ({
    boxHeader: css`
        display: inline-flex;
        align-items: center;
    `,
    boxIcon: css`
        display: inline-flex;
        align-items: center;
        margin-left: 10px;
    `,
    dividerFooter: css`
        margin-bottom: 20px;
    `,
}));

const TableAdvanced = ({ columns, data, renderRowSubComponent, renderFooterContent, offsetLeft, rowTo, pageSize, sortBy }) => {
    const {t} = useTranslation()
    const styles = useStyles();
    const tableBodyRef = useRef();
    const tableFooterRef = useRef();

    const scrollBarSize = useMemo(() => scrollbarWidth(), []);

    const defaultColumn = React.useMemo(
        () => ({
            minWidth: 30,
            width: 100,
            maxWidth: 400,
        }),
        []
    )

    const {
        getTableProps,
        headerGroups,
        page,
        visibleColumns,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        gotoPage,
        nextPage,
        previousPage,
        toggleRowExpanded,
        state: { pageIndex }
    } = useTable(
        {
            columns,
            data,
            autoResetExpanded: false,
            autoResetSortBy: false,
            initialState: {
                pageSize,
                sortBy
            },
            defaultColumn
        },
        useSortBy,
        useExpanded,
        useResizeColumns,
        useFlexLayout,
        usePagination
    );

    useLayoutEffect(() => {
        tableBodyRef.current.style.height = `calc(100vh - ${tableBodyRef.current.offsetTop + tableFooterRef.current.offsetHeight}px)`;
    }, []);

    const handleClickRowExpanded = rowId => () => toggleRowExpanded(rowId);

    return (
        <StyledTableContainer>
            <StyledTableWrapper>
                <StyledTable
                    {...getTableProps()}
                    offsetLeft={offsetLeft}
                    scrollBarSize={scrollBarSize}
                >
                    <StyledThead>
                        {headerGroups.map(headerGroup => (
                            <StyledTr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <StyledTh {...column.getHeaderProps([{ style: column.style }, column.getSortByToggleProps()])}>
                                        <Box
                                            as="span"
                                            customStyle={styles.boxHeader}
                                        >
                                            {column.render('Header')}
                                            <Box
                                                as="span"
                                                customStyle={styles.boxIcon}
                                            >
                                                {
                                                    column.isSorted
                                                        ? column.isSortedDesc
                                                            ? <Icon name="arrow-down" />
                                                            : <Icon name="arrow-up" />
                                                        : ''
                                                }
                                            </Box>
                                        </Box>
                                    </StyledTh>
                                ))}
                            </StyledTr>
                        ))}
                    </StyledThead>
                    <StyledTbody
                        ref={tableBodyRef}
                    >
                        {page.map(row => {
                            prepareRow(row);
                            const rowProps = row.getRowProps();

                            return (
                                <Fragment key={rowProps.key}>
                                    {
                                        (rowTo === null) ? (
                                            <StyledTrExpanded>
                                                <StyledTr
                                                    {...rowProps}
                                                    onClick={handleClickRowExpanded(row.id)}
                                                >
                                                    {row.cells.map(cell => {
                                                        return <StyledTd {...cell.getCellProps([{ style: cell.column.style }])}>{cell.render('Cell')}</StyledTd>
                                                    })}
                                                </StyledTr>
                                                {row.isExpanded && renderRowSubComponent({ row, rowProps, visibleColumns })}
                                            </StyledTrExpanded>
                                        ) : (
                                                <StyledTrLink to={`${rowTo}/${row.original?.id}`}>
                                                    <StyledTr
                                                        {...rowProps}
                                                        onClick={handleClickRowExpanded(row.id)}
                                                    >
                                                        {row.cells.map(cell => {
                                                            return <StyledTd {...cell.getCellProps([{ style: cell.column.style }])}>{cell.render('Cell')}</StyledTd>
                                                        })}
                                                    </StyledTr>
                                                </StyledTrLink>
                                            )
                                    }
                                </Fragment>
                            )
                        })}
                        {
                            page.length === 0 &&
                            <Typography align="center">
                                {t("Brak danych")}
                            </Typography>
                        }
                    </StyledTbody>
                </StyledTable>
                <StyledFooter ref={tableFooterRef}>
                    <Divider customStyle={styles.dividerFooter} />
                    <Row
                        between="xs"
                        center="xs"
                    >
                        <Col
                            xs
                        >
                            {renderFooterContent && renderFooterContent()}
                        </Col>
                        <Col>
                            <Pagination
                                pageIndex={pageIndex + 1}
                                pageLength={pageOptions.length === 0 ? 1 : pageOptions.length}
                                previousPage={previousPage}
                                canPreviousPage={canPreviousPage}
                                canNextPage={canNextPage}
                                nextPage={nextPage}
                                gotoPage={gotoPage}
                            />
                        </Col>
                    </Row>
                </StyledFooter>
            </StyledTableWrapper>
        </StyledTableContainer >
    );
};

TableAdvanced.propTypes = {
    columns: PropTypes.array,
    data: PropTypes.array,
    renderRowSubComponent: PropTypes.func,
    renderFooterContent: PropTypes.func,
    rowTo: PropTypes.string,
    pageSize: PropTypes.number,
    sortBy: PropTypes.array
};

TableAdvanced.defaultProps = {
    columns: [],
    data: [],
    offsetLeft: 0,
    rowTo: null,
    pageSize: 10,
    sortBy: []
};

export default React.memo(TableAdvanced);

export const Tr = ({ children, ...rest }) => (<StyledTr {...rest}>{children}</StyledTr>);
export const Td = ({ children, ...rest }) => (<StyledTd {...rest}>{children}</StyledTd>);
